/**
 * 個別説明 (briefings) の hooks。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §7
 */
import type {
  BriefingBookingStyle,
  BriefingCandidateEntry,
  BriefingRecord,
  BriefingSchedule,
  BriefingScheduleStyle,
  ConsiderationGroup,
  ConversionLevel,
  FormFieldType,
  Installments,
  PaymentMethod,
  SettingsBriefing,
  TradeInType,
} from '@goenhub/shared';
import { SETTINGS_BRIEFING_DEFAULTS, parseBriefingSlotValue } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useMemo } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { useMailContacts, useSaContacts, type MailContactRecord } from '@/hooks/useMailContacts';
import { db, functions } from '@/lib/firebase';
import { safeSetDoc } from '@/lib/safeWrite';

/** リードタイム設定 (締切判定用)。defaults で穴埋め済みの形。 */
export type BriefingLeadDays = Pick<SettingsBriefing, 'weekdayLeadDays' | 'holidayLeadDays'>;

function mergeLeadDays(data: Partial<SettingsBriefing> | null | undefined): BriefingLeadDays {
  return {
    weekdayLeadDays: data?.weekdayLeadDays ?? SETTINGS_BRIEFING_DEFAULTS.weekdayLeadDays,
    holidayLeadDays: data?.holidayLeadDays ?? SETTINGS_BRIEFING_DEFAULTS.holidayLeadDays,
  };
}

export interface BriefingScheduleRecord extends BriefingSchedule {
  id: string;
}

export interface BriefingRecordEntry extends BriefingRecord {
  id: string;
}

// ─── briefing_schedules ───

export function useBriefingSchedules() {
  const { user } = useAuth();
  // 外部SA は自分の担当枠のみ閲覧可 (firestore.rules で read を assigneeUid==uid に制限)。
  // rules はクエリ結果をフィルタしないため、クエリ側にも where を必須で付与する。
  const isExternalSa = user?.level === 'external' && !user?.isOwner;
  const uid = user?.uid;
  return useQuery({
    queryKey: ['briefing_schedules', isExternalSa ? `self:${uid}` : 'all'],
    enabled: !!user,
    queryFn: async (): Promise<BriefingScheduleRecord[]> => {
      const base = collection(db, 'briefing_schedules');
      const q =
        isExternalSa && uid
          ? query(
              base,
              where('assigneeUid', '==', uid),
              orderBy('date', 'desc'),
              orderBy('startTime', 'asc'),
            )
          : query(base, orderBy('date', 'desc'), orderBy('startTime', 'asc'));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as BriefingSchedule),
      }));
    },
  });
}

const findDeletedSchedulesFn = httpsCallable<{ ids: string[] }, { deletedIds: string[] }>(
  functions,
  'findDeletedBriefingSchedules',
);

/**
 * 候補日の枠 id のうち削除済みのものを返す (2026-10-13)。
 * 削除済みの枠は id の代わりに「削除された候補日」(BRIEFING_DELETED_SLOT_LABEL) と表記する。
 * 手元の枠一覧 (useBriefingSchedules) に無い id だけをサーバーへ問い合わせる。
 * 外部SA の一覧は自分の枠のみなので、「一覧に無い = 削除済み」とは限らない (他SA の枠がある)。
 * 問い合わせに失敗した場合は空集合を返す (呼び出し側は従来どおり id を表示する)。
 */
export function useDeletedBriefingScheduleIds(scheduleIds: string[]): Set<string> {
  const { data: schedules } = useBriefingSchedules();
  const unknownKey = useMemo(() => {
    if (!schedules) return '';
    const known = new Set(schedules.map((s) => s.id));
    return [...new Set(scheduleIds.filter((id) => id && !known.has(id)))].sort().join(',');
  }, [schedules, scheduleIds]);
  const { data } = useQuery({
    // 'briefing_schedules' 配下に置き、枠の作成・削除時の invalidate で再判定させる
    queryKey: ['briefing_schedules', 'deleted', unknownKey],
    enabled: unknownKey.length > 0,
    retry: false,
    queryFn: async (): Promise<string[]> => {
      const r = await findDeletedSchedulesFn({ ids: unknownKey.split(',').slice(0, 100) });
      return r.data.deletedIds;
    },
  });
  return useMemo(() => new Set(data ?? []), [data]);
}

const upsertScheduleFn = httpsCallable<
  {
    id?: string;
    date: string;
    startTime: string;
    endTime: string;
    style: BriefingScheduleStyle;
    zoomUrl?: string | null;
    zoomId?: string | null;
    location?: string | null;
    capacity: number;
    isActive: boolean;
    /** 管理者のみ指定可。担当者の uid。SA は無視され自分に固定される。 */
    assigneeUid?: string | null;
  },
  { id: string }
>(functions, 'upsertBriefingSchedule');

const deleteScheduleFn = httpsCallable<{ id: string }, { id: string }>(
  functions,
  'deleteBriefingSchedule',
);

export function useUpsertBriefingSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof upsertScheduleFn>[0]) => {
      const r = await upsertScheduleFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['briefing_schedules'] }),
  });
}

export function useDeleteBriefingSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await deleteScheduleFn({ id });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['briefing_schedules'] }),
  });
}

// ─── briefing_settings/{uid} (ユーザー個別の予約受付締切) ───

/** 自分の締切設定 (briefing_settings/{currentUid}) を defaults で穴埋めして返す。 */
export function useMyBriefingSettings() {
  const { user } = useAuth();
  const uid = user?.uid;
  const q = useQuery({
    queryKey: ['briefing_settings', uid ?? 'anon'],
    enabled: !!uid,
    queryFn: async (): Promise<SettingsBriefing | null> => {
      if (!uid) return null;
      const snap = await getDoc(doc(db, 'briefing_settings', uid));
      return snap.exists() ? (snap.data() as SettingsBriefing) : null;
    },
  });
  return { ...q, merged: mergeLeadDays(q.data) };
}

/** 自分の締切設定を保存する。 */
export function useUpdateMyBriefingSettings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (values: BriefingLeadDays) => {
      if (!user) throw new Error('認証が必要です');
      await safeSetDoc(
        doc(db, 'briefing_settings', user.uid),
        { ...values, updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['briefing_settings'] }),
  });
}

/**
 * 指定 uid 群の締切設定を Map<uid, BriefingLeadDays> で返す (管理画面の枠分類用)。
 * 未登録 uid は defaults で穴埋め。documentId() の in クエリで 30件ずつ取得。
 */
export function useBriefingSettingsMap(uids: string[]) {
  const sorted = Array.from(new Set(uids.filter(Boolean))).sort();
  return useQuery({
    queryKey: ['briefing_settings', 'map', sorted],
    enabled: sorted.length > 0,
    queryFn: async (): Promise<Map<string, BriefingLeadDays>> => {
      const map = new Map<string, BriefingLeadDays>();
      const col = collection(db, 'briefing_settings');
      for (let i = 0; i < sorted.length; i += 30) {
        const chunk = sorted.slice(i, i + 30);
        const snap = await getDocs(query(col, where(documentId(), 'in', chunk)));
        snap.docs.forEach((d) => map.set(d.id, mergeLeadDays(d.data() as SettingsBriefing)));
      }
      // 未登録 uid は defaults。
      for (const u of sorted) {
        if (!map.has(u)) map.set(u, mergeLeadDays(null));
      }
      return map;
    },
  });
}

// ─── confirmBriefingDate ───

const confirmBriefingDateFn = httpsCallable<
  {
    dealId: string;
    briefingScheduleId: string;
    assigneeUid?: string;
    selectedStyle?: BriefingBookingStyle;
    /** 'additional'=新しい個別説明として追加 / 'reschedule'=既存セッションの日程変更 */
    mode?: 'additional' | 'reschedule';
    /** mode='reschedule' のとき置き換える対象セッションの枠 id */
    replaceScheduleId?: string | null;
  },
  { dealId: string; briefingScheduleId: string }
>(functions, 'confirmBriefingDate');

// ─── 外部SA 向け: 個別説明の申込フォーム回答 (2026-09-09) ───

/** getBriefingSubmissionsForSa の返却型 (functions/src/briefings/submissions.ts と同期)。 */
export interface BriefingSubmissionForSa {
  id: string;
  formId: string;
  /** ISO 文字列 */
  submittedAt: string | null;
  email: string | null;
  briefingParticipantCount: number | null;
  values: Record<string, string | string[]>;
}
export interface BriefingSubmissionFieldForSa {
  id: string;
  label: string;
  fieldType: FormFieldType;
}
export interface BriefingSubmissionScheduleForSa {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  style: BriefingScheduleStyle;
  location: string | null;
}
export interface BriefingSubmissionsForSaResult {
  submissions: BriefingSubmissionForSa[];
  formFields: Record<string, BriefingSubmissionFieldForSa[]>;
  schedules: Record<string, BriefingSubmissionScheduleForSa>;
}

/**
 * 外部SA が「自分に関係する申込」の回答だけを取得する Callable。
 * form_submissions は rules で owner/editor 限定のため、SA はこの経路のみ。
 * 開示判定は shared の visibleBriefingSubmissionIdsForSa (申込単位)。
 */
export const getBriefingSubmissionsForSaFn = httpsCallable<
  { dealId: string },
  BriefingSubmissionsForSaResult
>(functions, 'getBriefingSubmissionsForSa');

// ─── 個別説明ページの顧客 (2026-10-16) ───

/** getBriefingContactsForSa の返却型 (functions/src/briefings/contacts.ts と同期)。 */
interface BriefingContactForSa {
  id: string;
  name: string;
  lastNameKana: string | null;
  firstNameKana: string | null;
  kind: MailContactRecord['kind'] | null;
  defaultEmail: string | null;
  phone: string | null;
}

const getBriefingContactsForSaFn = httpsCallable<
  { dealIds: string[] },
  { contacts: BriefingContactForSa[] }
>(functions, 'getBriefingContactsForSa');

/**
 * 個別説明ページの顧客。外部SA が窓口経由で受け取る顧客は表示用の項目しか持たないため、
 * id / name 以外は省略されうる。
 */
export type BriefingContactRecord = Pick<MailContactRecord, 'id' | 'name'> &
  Partial<Omit<MailContactRecord, 'id' | 'name'>>;

/** 窓口 1 回で問い合わせる商談の上限 (functions 側 MAX_DEAL_IDS と同じ)。 */
const BRIEFING_CONTACTS_CHUNK = 100;

/**
 * 個別説明ページで顧客名を解決するための顧客一覧 (2026-10-16)。
 * - 外部SA 以外: 従来どおり全件 (useMailContacts)。
 * - 外部SA: 全件の read は rules で拒否されるため、次の 2 つを合わせて返す。
 *     ① 自分の顧客 (ownerSaUid === 自分。useSaContacts)
 *     ② ①に無い顧客のうち、自分に関係する申込がある商談の顧客 (getBriefingContactsForSa。
 *        開示判定はサーバー側。氏名・フリガナ・種別・メール・電話のみ)
 *   ②の問い合わせに失敗した場合は①だけを返す (その顧客は「(顧客名未確定)」表示のまま)。
 */
export function useBriefingContacts(
  deals: ReadonlyArray<{ id: string; contactId: string | null | undefined }> | undefined,
): BriefingContactRecord[] | undefined {
  const { user } = useAuth();
  const isExternalSa = user?.level === 'external' && !user?.isOwner;
  const all = useMailContacts(undefined, { enabled: !!user && !isExternalSa });
  const own = useSaContacts(isExternalSa ? user?.uid : null);

  // ①で解決できない顧客を持つ商談だけを問い合わせる
  const missingKey = useMemo(() => {
    if (!isExternalSa || !own.data || !deals) return '';
    const ownIds = new Set(own.data.map((c) => c.id));
    return [
      ...new Set(deals.filter((d) => d.contactId && !ownIds.has(d.contactId)).map((d) => d.id)),
    ]
      .sort()
      .join(',');
  }, [isExternalSa, own.data, deals]);
  const lookup = useQuery({
    queryKey: ['mail_contacts', 'briefing_sa', user?.uid ?? null, missingKey],
    enabled: missingKey.length > 0,
    retry: false,
    queryFn: async (): Promise<BriefingContactForSa[]> => {
      const ids = missingKey.split(',');
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += BRIEFING_CONTACTS_CHUNK) {
        chunks.push(ids.slice(i, i + BRIEFING_CONTACTS_CHUNK));
      }
      const results = await Promise.all(
        chunks.map((dealIds) => getBriefingContactsForSaFn({ dealIds })),
      );
      return results.flatMap((r) => r.data.contacts);
    },
  });

  return useMemo(() => {
    if (!isExternalSa) return all.data;
    if (!own.data) return undefined;
    const ownIds = new Set(own.data.map((c) => c.id));
    const extra: BriefingContactRecord[] = (lookup.data ?? [])
      .filter((c) => !ownIds.has(c.id))
      .map((c) => ({
        id: c.id,
        name: c.name,
        lastNameKana: c.lastNameKana,
        firstNameKana: c.firstNameKana,
        kind: c.kind ?? undefined,
        defaultEmail: c.defaultEmail ?? undefined,
        phone: c.phone,
      }));
    return [...own.data, ...extra];
  }, [isExternalSa, all.data, own.data, lookup.data]);
}

export function useConfirmBriefingDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      briefingScheduleId: string;
      assigneeUid?: string;
      mode?: 'additional' | 'reschedule';
      replaceScheduleId?: string | null;
      /** 「両方可」枠を確定する際の開催スタイル。 */
      selectedStyle?: BriefingBookingStyle;
    }) => {
      const r = await confirmBriefingDateFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['briefing_schedules'] });
    },
  });
}

// ─── 旧データ用: form_submissions の回答から候補日を復元 ───

/**
 * Deal に候補データ (briefingCandidates / briefingCandidateScheduleIds) が無い
 * 旧申込 (2026-07-23 修正前) 向けフォールバック。
 * sourceFormSubmissionIds の回答値のうち briefing_schedules の実在 id と一致する
 * ものを候補日として復元する (フォーム構成に依存しない id 照合方式)。
 * 戻り値: Map<dealId, BriefingCandidateEntry[]>
 */
export function useSubmissionBriefingCandidateFallback(
  deals: Array<{ id: string; sourceFormSubmissionIds?: string[] | null }>,
  scheduleIds: string[],
) {
  const targets = deals.map((d) => ({
    id: d.id,
    subs: (d.sourceFormSubmissionIds ?? []).slice(0, 10),
  }));
  const dealKey = targets
    .map((t) => `${t.id}:${t.subs.join('|')}`)
    .sort()
    .join(',');
  const schedKey = [...scheduleIds].sort().join(',');
  return useQuery({
    queryKey: ['briefing_candidate_fallback', dealKey, schedKey],
    enabled: targets.length > 0 && scheduleIds.length > 0,
    queryFn: async (): Promise<Map<string, BriefingCandidateEntry[]>> => {
      const schedSet = new Set(scheduleIds);
      const map = new Map<string, BriefingCandidateEntry[]>();
      // 同一 submission の重複取得を避ける (deal 間で共有され得る)
      const subCache = new Map<string, Record<string, unknown> | null>();
      const getSub = async (sid: string) => {
        if (subCache.has(sid)) return subCache.get(sid) ?? null;
        let data: Record<string, unknown> | null = null;
        try {
          const snap = await getDoc(doc(db, 'form_submissions', sid));
          data = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
        } catch {
          // 権限不足等は無視 (候補なし扱い)
          data = null;
        }
        subCache.set(sid, data);
        return data;
      };
      for (const t of targets) {
        const entries: BriefingCandidateEntry[] = [];
        for (const sid of t.subs) {
          const data = await getSub(sid);
          const values = (data?.values ?? {}) as Record<string, string | string[]>;
          const rawCount = data?.briefingParticipantCount;
          const participantCount =
            typeof rawCount === 'number' && Number.isFinite(rawCount)
              ? Math.min(99, Math.max(1, Math.floor(rawCount)))
              : 1;
          for (const v of Object.values(values)) {
            const arr = Array.isArray(v) ? v : [v];
            for (const raw of arr) {
              if (typeof raw !== 'string') continue;
              // 「両方可」枠は `${枠id}::online` 形式で保存されるため分解する
              const { scheduleId, style } = parseBriefingSlotValue(raw);
              if (schedSet.has(scheduleId) && !entries.some((e) => e.scheduleId === scheduleId)) {
                entries.push({
                  scheduleId,
                  formSubmissionId: sid,
                  status: 'pending',
                  decidedAt: null,
                  createdAt: '',
                  participantCount,
                  selectedStyle: style,
                });
              }
            }
          }
        }
        if (entries.length > 0) map.set(t.id, entries);
      }
      return map;
    },
  });
}

// ─── declineBriefingCandidate (候補日の拒否) ───

const declineBriefingCandidateFn = httpsCallable<
  {
    dealId: string;
    briefingScheduleId: string;
    /** 旧データ復元候補用: 由来 submission id (Deal 未保存候補の declined 追記に使う) */
    formSubmissionId?: string | null;
  },
  { dealId: string; briefingScheduleId: string }
>(functions, 'declineBriefingCandidate');

export function useDeclineBriefingCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      briefingScheduleId: string;
      formSubmissionId?: string | null;
    }) => {
      const r = await declineBriefingCandidateFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['briefing_candidate_fallback'] });
    },
  });
}

// ─── briefing_records ───

/** 全商談の記録 1 件。親の商談 id を併記する (collectionGroup で取得するため)。 */
export interface BriefingRecordWithDeal extends BriefingRecordEntry {
  dealId: string;
}

/**
 * 全商談の briefing_records を一括取得 (実施状況タブ用、2026-10-07)。
 * collectionGroup の read は firestore.rules の `{path=**}/briefing_records` で許可。
 */
export function useAllBriefingRecords(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['briefing_records', 'all'],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<BriefingRecordWithDeal[]> => {
      const snap = await getDocs(collectionGroup(db, 'briefing_records'));
      return snap.docs.flatMap((d) => {
        const dealId = d.ref.parent.parent?.id;
        if (!dealId) return [];
        return [{ id: d.id, dealId, ...(d.data() as BriefingRecord) }];
      });
    },
  });
}

export function useBriefingRecords(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['briefing_records', dealId],
    enabled: !!dealId,
    queryFn: async (): Promise<BriefingRecordEntry[]> => {
      if (!dealId) return [];
      const snap = await getDocs(
        query(collection(db, 'deals', dealId, 'briefing_records'), orderBy('conductedAt', 'desc')),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as BriefingRecord),
      }));
    },
  });
}

const saveBriefingRecordFn = httpsCallable<
  {
    dealId: string;
    /** 既存記録の更新 (2026-07-23)。実施担当者 (conductedBy) 本人のみ可。 */
    recordId?: string | null;
    /** 'draft'=下書き保存 / 'completed'=個別説明終了 (省略時 completed)。 */
    status?: 'draft' | 'completed';
    /** 2026-05-28: schedule なしの手動追加では省略可 (null 保存)。 */
    briefingScheduleId?: string | null;
    conversionLevel: ConversionLevel;
    interestedProductIds?: string[];
    consideredProducts?: ConsiderationGroup[];
    inquiries?: string;
    introductionImage?: string;
    concerns?: string;
    purchaseTimeline?: string;
    paymentMethod: PaymentMethod;
    installments?: Installments | null;
    hasTradeIn: boolean;
    tradeInType?: TradeInType | null;
    saleAware: boolean;
    saleName?: string | null;
    hasIncentive: boolean;
    incentiveName?: string | null;
    nextAction?: string;
    supplementaryNotes?: string;
  },
  { dealId: string; recordId: string }
>(functions, 'saveBriefingRecord');

export function useSaveBriefingRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof saveBriefingRecordFn>[0]) => {
      const r = await saveBriefingRecordFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (!vars) return;
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['briefing_records', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['briefing_records', 'all'] });
    },
  });
}

// ─── 未実施マーク (v3.4.1 / 2026-08-07) ───

const markNotConductedFn = httpsCallable<
  { dealId: string; note?: string | null },
  { dealId: string; recordId: string }
>(functions, 'markBriefingNotConducted');

/** 確定済みの個別説明を「未実施」としてマーク (not_conducted 記録を作成)。
 * 完了記録がある deal には不可。代行料の請求対象からは除外される。 */
export function useMarkBriefingNotConducted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof markNotConductedFn>[0]) => {
      const r = await markNotConductedFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (!vars) return;
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['briefing_records', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['briefing_records', 'all'] });
      qc.invalidateQueries({ queryKey: ['briefing_records_flags'] });
      qc.invalidateQueries({ queryKey: ['unbilled_briefings'] });
    },
  });
}

const undoNotConductedFn = httpsCallable<{ dealId: string; recordId: string }, { ok: true }>(
  functions,
  'undoBriefingNotConducted',
);

/** 未実施マークの取消 (not_conducted 記録を削除して確定状態に戻す)。 */
export function useUndoBriefingNotConducted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof undoNotConductedFn>[0]) => {
      const r = await undoNotConductedFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (!vars) return;
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['briefing_records', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['briefing_records', 'all'] });
      qc.invalidateQueries({ queryKey: ['briefing_records_flags'] });
      qc.invalidateQueries({ queryKey: ['unbilled_briefings'] });
    },
  });
}
